import { atom, PrimitiveAtom } from 'jotai';
import { pendingQueue } from 'shared/utils/pending-queue';
import { safeAtomFamily } from 'shared/utils/safe-atom-family';
import { IHashtagInfo, IHashtagUnavailableInfo } from 'shared/models/hashtag';
import { TArticleDetail } from 'shared/models/article';
import { Unreachable } from '@lemon8/web-app-shared/util';

export interface IHashtagPosts {
  items: TArticleDetail[];
  hasMore: boolean;
  sessionImprId: string;
  maxBehotTime: number;
  minBehotTime: number;
}

type HashtagInfoStore = { [key: string]: IHashtagInfo | IHashtagUnavailableInfo | undefined };
type HashtagPostsStore = { [key: string]: IHashtagPosts | undefined };

export const $HashtagInfoStore: PrimitiveAtom<HashtagInfoStore> = atom<HashtagInfoStore>({});
export const $HashtagPopularPostStore: PrimitiveAtom<HashtagPostsStore> = atom<HashtagPostsStore>({});
export const $HashtagRecentPostStore: PrimitiveAtom<HashtagPostsStore> = atom<HashtagPostsStore>({});

export const $HashtagInfo = safeAtomFamily((hashtagId: string) => {
  const a = atom(
    (get) => {
      const data = get($HashtagInfoStore)[hashtagId];
      if (!data) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Promise((resolve) => pendingQueue.push(resolve));
      }
      return data;
    },
    (get, set, input: IHashtagInfo | IHashtagUnavailableInfo) => {
      const old = get($HashtagInfoStore);
      set($HashtagInfoStore, { ...old, [hashtagId]: input });
    },
  );
  a.debugLabel = `$HashtagInfo+${encodeURIComponent(hashtagId)}`;
  return a;
});

export const $HashtagPopularPost = safeAtomFamily((hashtagId: string) => {
  const a = atom(
    (get) => {
      const data = get($HashtagPopularPostStore)[hashtagId];
      if (!data) {
        return Unreachable('no data in $HashtagPopularPost');
      }
      return data;
    },
    (get, set, input: IHashtagPosts) => {
      const old = get($HashtagPopularPostStore);
      set($HashtagPopularPostStore, { ...old, [hashtagId]: input });
    },
  );
  a.debugLabel = `$HashtagPopularPost+${encodeURIComponent(hashtagId)}`;
  return a;
});

export const $HashtagPopularPostLoadmore = safeAtomFamily((hashtagId: string) => {
  const a = atom<null, [IHashtagPosts], void>(null, async (get, set, input: IHashtagPosts) => {
    const store = get($HashtagPopularPostStore);
    const old = await store[hashtagId];

    if (old) {
      set($HashtagPopularPostStore, {
        ...store,
        [hashtagId]: {
          ...old,
          ...input,
          items: [...old.items, ...input.items],
        },
      });
    }
  });

  a.debugLabel = `$HashtagPopularPostLoadmore+${encodeURIComponent(hashtagId)}`;

  return a;
});

export const $HashtagRecentPost = safeAtomFamily((hashtagId: string) => {
  const a = atom(
    (get) => {
      const data = get($HashtagRecentPostStore)[hashtagId];
      if (!data) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Promise((resolve) => pendingQueue.push(resolve));
      }
      return data;
    },
    (get, set, input: IHashtagPosts) => {
      const old = get($HashtagRecentPostStore);
      set($HashtagRecentPostStore, { ...old, [hashtagId]: input });
    },
  );

  a.debugLabel = `$HashtagRecentPost+${encodeURIComponent(hashtagId)}`;
  return a;
});

export const $HashtagRecentPostLoadmore = safeAtomFamily((hashtagId: string) => {
  const a = atom<null, [IHashtagPosts], void>(null, async (get, set, input: IHashtagPosts) => {
    const store = get($HashtagRecentPostStore);
    const old = await store[hashtagId];

    if (old) {
      set($HashtagRecentPostStore, {
        ...store,
        [hashtagId]: {
          ...old,
          ...input,
          items: [...old.items, ...input.items],
        },
      });
    }
  });

  a.debugLabel = `$HashtagRecentPostLoadmore+${encodeURIComponent(hashtagId)}`;

  return a;
});
